/**
 * Exportação "HTML interativo": um arquivo .html único e autocontido, montado no navegador a partir do recorte que a
 * tela mostra (snapshot), que abre sem o Hive e sem rede.
 *
 * `buildExportHtml(snapshot, meta)` devolve o texto do arquivo:
 * - `snapshot`: `{ title, filters?, kind: 'table' | 'map' | 'dashboard', data }`
 *   - table: `{ columns: { id, label, numeric? }[], rows: { [colId]: valor }[] }` → tabela com busca e ordenação;
 *   - map: `{ nodes: { id, label, x, y, tone?, layers?, detail? }[], edges?: { from, to }[], layers?: { id, label,
 *     color, on? }[] }` → desenho com arrastar/zoom, camadas liga/desliga e detalhe ao clicar num nó;
 *   - dashboard: `{ cards: { label, value, foot?, tone? }[], charts?: { title, items: { label, value }[] }[], lists?:
 *     { title, items: { tag?, tone?, title, where? }[] }[] }` → cartões, barras e listas animados;
 * - `meta`: `{ lang, brandColor?, logo?, brandName?, texts }` — `texts` já traduzidos (rodapé "Exportado do Hive em … por …,
 *   filtros: …", busca, zoom, tema…); `logo` é o logotipo completo da empresa como data URI (vai no cabeçalho, sem rede); sem
 *   ele, o cabeçalho mostra `brandName` (nome da empresa ou Hive).
 *
 * Segurança: todo dado vai como JSON num `<script type="application/json">` (com `<`, `>`, `&` e separadores de linha
 * escapados) e o script do arquivo o insere só com `textContent`/atributos — nunca `innerHTML`. O arquivo traz a
 * própria política de conteúdo (nada de fora: sem rede). Animações de entrada respeitam `prefers-reduced-motion`.
 */
import { isHexColor, mix } from './theme';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf' | 'png' | 'svg' | 'html';
export type ExportTone = 'ok' | 'warn' | 'crit' | 'info' | 'pred' | 'idle';
type Cell = string | number | boolean | null | undefined;

export interface ExportTableData {
  columns: { id: string; label: string; numeric?: boolean }[];
  rows: Array<Record<string, Cell>>;
}

export interface ExportMapNode {
  id: string;
  label: string;
  x: number;
  y: number;
  tone?: ExportTone;
  /** Camadas que marcam este nó (ids de `layers`). */
  layers?: string[];
  /** Pares rótulo → valor mostrados no detalhe ao clicar. */
  detail?: Record<string, Cell>;
}

export interface ExportMapData {
  nodes: ExportMapNode[];
  edges?: { from: string; to: string }[];
  layers?: { id: string; label: string; color: string; on?: boolean }[];
}

export interface ExportDashboardData {
  cards: { label: string; value: number | string; foot?: string; tone?: ExportTone }[];
  charts?: { title: string; items: { label: string; value: number }[] }[];
  lists?: { title: string; items: { tag?: string; tone?: ExportTone; title: string; where?: string }[] }[];
}

export type ExportSnapshot = { title: string; filters?: Record<string, string> } & (
  | { kind: 'table'; data: ExportTableData }
  | { kind: 'map'; data: ExportMapData }
  | { kind: 'dashboard'; data: ExportDashboardData }
);

export interface ExportTexts {
  /** Rodapé já pronto: "Exportado do Hive em 29/10/2026 14:05 por Ana, filtros: …". */
  footer: string;
  search: string;
  /** "{n} de {total} linhas". */
  rows: string;
  layers: string;
  zoomIn: string;
  zoomOut: string;
  reset: string;
  theme: string;
  close: string;
  filters: string;
}

export interface ExportMeta {
  lang: string;
  brandColor?: string | null;
  /** logotipo completo da empresa como data URI (PNG, JPEG, WebP ou SVG) */
  logo?: string | null;
  /** nome no cabeçalho quando não há logotipo */
  brandName?: string | null;
  texts: ExportTexts;
}

/** Só imagem embutida (data:image/…;base64) entra no arquivo: nada de endereço de fora, que a política do arquivo bloqueia. */
export function safeLogoDataUri(v: unknown): string | null {
  return typeof v === 'string' && v.length <= 3_000_000 && /^data:image\/(png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/]+=*$/.test(v) ? v : null;
}

/** JSON seguro dentro de <script>: nada nele fecha a tag nem vira marcação. */
export function safeJson(v: unknown): string {
  return JSON.stringify(v)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

/** Nome de arquivo a partir do título: "inicio-2026-10-29-1405.html". */
export function exportFileName(title: string, ext: string, at = new Date()): string {
  const base =
    title
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) || 'hive';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${base}-${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())}-${p(at.getHours())}${p(at.getMinutes())}.${ext}`;
}

/** CSV (separador ";" para o Excel em pt-BR e es; vírgula em inglês), com BOM para os acentos. */
export function tableToCsv(d: ExportTableData, lang = 'pt-BR'): string {
  const sep = lang === 'en' ? ',' : ';';
  const q = (v: Cell) => {
    const s = v == null ? '' : String(v);
    // fórmula no começo vira texto (injeção de fórmula em planilha)
    const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
    return /[";,\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const lines = [d.columns.map((c) => q(c.label)).join(sep), ...d.rows.map((r) => d.columns.map((c) => q(r[c.id])).join(sep))];
  return '﻿' + lines.join('\r\n');
}

const CSS = `
:root{--bg:#F4F6F5;--sf:#fff;--sf2:#EEF2F0;--ln:#DCE2E0;--fg:#16211F;--fg2:#4A5653;--mu:#5D6A67;--ac:ACCENT;--acf:#fff;
--ok:#2E8B73;--warn:#D98E04;--crit:#B42318;--info:#1E4E8C;--idle:#8A9693;--pred:#D98E04;color-scheme:light}
@media (prefers-color-scheme:dark){:root:not([data-theme=light]){--bg:#0F1513;--sf:#161D1B;--sf2:#1D2623;--ln:#2C3834;--fg:#E3EAE7;--fg2:#C2CDC9;--mu:#97A6A1;--ac:ACCDARK;--acf:#0B1A16;--info:#6DB3F2;color-scheme:dark}}
:root[data-theme=dark]{--bg:#0F1513;--sf:#161D1B;--sf2:#1D2623;--ln:#2C3834;--fg:#E3EAE7;--fg2:#C2CDC9;--mu:#97A6A1;--ac:ACCDARK;--acf:#0B1A16;--info:#6DB3F2;color-scheme:dark}
*{box-sizing:border-box}body{margin:0;background:var(--bg);color:var(--fg);font:14px/1.45 "IBM Plex Sans","Segoe UI",system-ui,-apple-system,Roboto,sans-serif}
#app{max-width:1280px;margin:0 auto;padding:20px 16px 28px;display:flex;flex-direction:column;gap:14px}
.hd{display:flex;align-items:center;gap:12px;flex-wrap:wrap;border-bottom:3px solid var(--ac);padding-bottom:10px}.hd h1{margin:0;font-size:22px;flex:1;min-width:200px}
.hd .logo{display:block;max-height:40px;max-width:200px;object-fit:contain}.hd .brand{font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--ac)}
.btn{height:32px;padding:0 12px;border:1px solid var(--ln);border-radius:8px;background:var(--sf);color:var(--fg);font:inherit;font-size:13px;cursor:pointer}
.btn:hover{border-color:var(--ac)}.btn:focus-visible,input:focus-visible,[tabindex]:focus-visible{outline:2px solid var(--ac);outline-offset:2px}
.filters{list-style:none;margin:0;padding:0;display:flex;flex-wrap:wrap;gap:6px}.filters li{background:var(--sf2);border:1px solid var(--ln);border-radius:12px;padding:2px 10px;font-size:12px;color:var(--fg2)}
.bar{display:flex;align-items:center;gap:10px;flex-wrap:wrap}.bar input[type=search]{height:34px;min-width:240px;flex:1;max-width:420px;border:1px solid var(--ln);border-radius:8px;padding:0 10px;background:var(--sf);color:var(--fg);font:inherit}
.muted{color:var(--mu);font-size:12px}
.tw{overflow:auto;max-height:75vh;background:var(--sf);border:1px solid var(--ln);border-radius:10px}
table{width:100%;border-collapse:collapse;font-size:13px}th{position:sticky;top:0;background:var(--sf2);text-align:left;font-size:12px;color:var(--fg2);padding:0;border-bottom:1px solid var(--ln)}
th button{all:unset;cursor:pointer;display:block;padding:10px 12px;font-weight:600}th button:focus-visible{outline:2px solid var(--ac)}
td{padding:9px 12px;border-bottom:1px solid var(--ln);white-space:nowrap}td.num{text-align:right;font-variant-numeric:tabular-nums}tbody tr:hover td{background:var(--sf2)}
.anim{animation:in .35s ease both}@keyframes in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.mapwrap{display:flex;gap:14px;flex-wrap:wrap;align-items:flex-start}.mapbox{flex:999 1 560px;min-width:0;height:70vh;background:var(--sf);border:1px solid var(--ln);border-radius:10px;overflow:hidden;touch-action:none;cursor:grab}
.mapbox svg{width:100%;height:100%;display:block}.mapbox:active{cursor:grabbing}
.node{cursor:pointer}.node circle.core{stroke:var(--sf);stroke-width:2}.node text{fill:var(--fg);font-size:11px;text-anchor:middle;pointer-events:none}
.node.sel circle.core{stroke:var(--fg);stroke-width:3}.edge{stroke:var(--ln);stroke-width:1.5}
.layers{display:flex;flex-wrap:wrap;gap:8px;align-items:center}.layers label{display:inline-flex;gap:6px;align-items:center;font-size:13px;border:1px solid var(--ln);border-radius:15px;padding:3px 10px;background:var(--sf);cursor:pointer}
.sw{width:10px;height:10px;border-radius:3px;display:inline-block}
.detail{flex:1 1 300px;max-width:400px;background:var(--sf);border:1px solid var(--ln);border-radius:10px;padding:14px 16px}.detail[hidden]{display:none}
.detail h2{margin:0 0 8px;font-size:17px}.detail dl{display:grid;grid-template-columns:auto 1fr;gap:4px 12px;margin:0;font-size:13px}.detail dt{color:var(--mu)}.detail dd{margin:0;overflow-wrap:anywhere}
.cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px}.card{background:var(--sf);border:1px solid var(--ln);border-radius:10px;padding:14px 16px;display:flex;flex-direction:column;gap:4px}
.card .l{font-size:12px;color:var(--mu)}.card .v{font-size:26px;font-weight:600;font-variant-numeric:tabular-nums}.card .f{font-size:12px;color:var(--mu)}
.tone-crit{color:var(--crit)}.tone-warn,.tone-pred{color:var(--warn)}.tone-ok{color:var(--ok)}
.panels{display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:12px}.panel{background:var(--sf);border:1px solid var(--ln);border-radius:10px;padding:14px 16px}.panel h2{margin:0 0 10px;font-size:15px}
.hb{display:grid;grid-template-columns:minmax(0,40%) 1fr auto;gap:8px;align-items:center;font-size:13px;margin:4px 0}.hb .t{height:10px;background:var(--sf2);border-radius:5px;overflow:hidden}
.hb .f{display:block;height:100%;width:0;background:var(--ac);border-radius:5px;transition:width .8s ease}
.li{display:flex;gap:10px;align-items:center;padding:8px 0;border-bottom:1px solid var(--ln);font-size:13px}.tag{font-size:11px;font-weight:700;padding:3px 8px;border-radius:10px;background:var(--sf2)}
.ft{font-size:12px;color:var(--mu);border-top:1px solid var(--ln);padding-top:10px}
@media (prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}
@media print{.btn,.bar input{display:none}.tw{max-height:none;overflow:visible}}
`;

/* script do arquivo exportado: só textContent e atributos (nunca innerHTML) */
const RUNTIME = `(function(){'use strict';
var P=JSON.parse(document.getElementById('hive-data').textContent),S=P.snapshot,T=P.texts;
var reduce=!!(window.matchMedia&&matchMedia('(prefers-reduced-motion: reduce)').matches);
var NS='http://www.w3.org/2000/svg';
var TONES={ok:'var(--ok)',warn:'var(--warn)',crit:'var(--crit)',info:'var(--info)',pred:'var(--pred)',idle:'var(--idle)'};
function el(t,c,x){var e=document.createElement(t);if(c)e.className=c;if(x!=null)e.textContent=String(x);return e}
function sv(t,a){var e=document.createElementNS(NS,t);for(var k in a)e.setAttribute(k,String(a[k]));return e}
function btn(x,f){var b=el('button','btn',x);b.type='button';b.onclick=f;return b}
var root=document.getElementById('app');
var hd=el('header','hd'),B=P.brand||{};
if(B.logo){var lg=el('img','logo');lg.alt=B.name||'';lg.setAttribute('src',B.logo);hd.appendChild(lg)}else if(B.name)hd.appendChild(el('span','brand',B.name));
hd.appendChild(el('h1',null,S.title));
hd.appendChild(btn(T.theme,function(){var d=document.documentElement,dark=d.getAttribute('data-theme')?d.getAttribute('data-theme')==='dark':!!(window.matchMedia&&matchMedia('(prefers-color-scheme: dark)').matches);d.setAttribute('data-theme',dark?'light':'dark')}));
root.appendChild(hd);
var fk=S.filters?Object.keys(S.filters):[];
if(fk.length){var fl=el('ul','filters');fl.setAttribute('aria-label',T.filters);fk.forEach(function(k){fl.appendChild(el('li',null,k+': '+S.filters[k]))});root.appendChild(fl)}
var main=el('main');root.appendChild(main);
function table(d){
 var bar=el('div','bar'),q=el('input'),cnt=el('span','muted');q.type='search';q.placeholder=T.search;q.setAttribute('aria-label',T.search);bar.appendChild(q);bar.appendChild(cnt);main.appendChild(bar);
 var tw=el('div','tw'),tb=el('table'),th=el('thead'),hr=el('tr'),body=el('tbody'),so={i:-1,d:1},ths=[];
 tb.setAttribute('aria-label',S.title);
 d.columns.forEach(function(c,i){var h=el('th');h.setAttribute('scope','col');h.setAttribute('aria-sort','none');h.appendChild(btn(c.label,function(){so.d=so.i===i?-so.d:1;so.i=i;draw()}));h.firstChild.className='';ths.push(h);hr.appendChild(h)});
 th.appendChild(hr);tb.appendChild(th);tb.appendChild(body);tw.appendChild(tb);main.appendChild(tw);
 function v(r,c){var x=r[c.id];return x==null?'':x}
 function draw(){var term=q.value.trim().toLowerCase();
  var rows=d.rows.filter(function(r){return !term||d.columns.some(function(c){return String(v(r,c)).toLowerCase().indexOf(term)>=0})});
  if(so.i>=0){var c=d.columns[so.i];rows=rows.slice().sort(function(a,b){var x=v(a,c),y=v(b,c);if(typeof x==='number'&&typeof y==='number')return(x-y)*so.d;return String(x).localeCompare(String(y),P.lang,{numeric:true})*so.d})}
  ths.forEach(function(h,i){h.setAttribute('aria-sort',i===so.i?(so.d>0?'ascending':'descending'):'none')});
  body.textContent='';rows.forEach(function(r,n){var tr=el('tr',reduce||n>40?null:'anim');if(!reduce&&n<=40)tr.style.animationDelay=(n*12)+'ms';d.columns.forEach(function(c){tr.appendChild(el('td',c.numeric?'num':null,v(r,c)))});body.appendChild(tr)});
  cnt.textContent=T.rows.replace('{n}',String(rows.length)).replace('{total}',String(d.rows.length))}
 q.addEventListener('input',draw);draw()}
function map(d){
 var on={},ly=d.layers||[];ly.forEach(function(l){on[l.id]=l.on!==false});
 var bar=el('div','bar');
 if(ly.length){var lw=el('div','layers');lw.setAttribute('role','group');lw.setAttribute('aria-label',T.layers);ly.forEach(function(l){var lb=el('label'),cb=el('input'),sw=el('span','sw');cb.type='checkbox';cb.checked=on[l.id];sw.style.background=l.color;cb.addEventListener('change',function(){on[l.id]=cb.checked;paint()});lb.appendChild(cb);lb.appendChild(sw);lb.appendChild(document.createTextNode(l.label));lw.appendChild(lb)});bar.appendChild(lw)}
 var k=1,tx=0,ty=0;
 bar.appendChild(btn(T.zoomIn,function(){zoom(1.25)}));bar.appendChild(btn(T.zoomOut,function(){zoom(0.8)}));bar.appendChild(btn(T.reset,function(){k=1;tx=0;ty=0;apply()}));
 main.appendChild(bar);
 var wrap=el('div','mapwrap'),box=el('div','mapbox'),det=el('aside','detail');det.hidden=true;det.setAttribute('aria-live','polite');
 var xs=d.nodes.map(function(n){return n.x}),ys=d.nodes.map(function(n){return n.y});
 var x0=Math.min.apply(null,xs.concat([0]))-60,y0=Math.min.apply(null,ys.concat([0]))-60,w=Math.max.apply(null,xs.concat([0]))-x0+60,h=Math.max.apply(null,ys.concat([0]))-y0+80;
 var s=sv('svg',{viewBox:x0+' '+y0+' '+w+' '+h,role:'img'});s.setAttribute('aria-label',S.title);
 var g=sv('g',{});s.appendChild(g);
 var by={};d.nodes.forEach(function(n){by[n.id]=n});
 (d.edges||[]).forEach(function(e){var a=by[e.from],b=by[e.to];if(a&&b)g.appendChild(sv('line',{x1:a.x,y1:a.y,x2:b.x,y2:b.y,'class':'edge'}))});
 var marks=[],sel=null;
 d.nodes.forEach(function(n,i){var ng=sv('g',{'class':'node'+(reduce?'':' anim'),tabindex:0,role:'button'});ng.setAttribute('aria-label',n.label);if(!reduce)ng.style.animationDelay=Math.min(i*8,600)+'ms';
  ng.appendChild(sv('circle',{cx:n.x,cy:n.y,r:14,'class':'core',fill:TONES[n.tone]||'var(--ac)'}));
  (n.layers||[]).forEach(function(id,j){var l=ly.filter(function(x){return x.id===id})[0];if(!l)return;var m=sv('circle',{cx:n.x+12-j*8,cy:n.y-12,r:5,fill:l.color});m.setAttribute('data-layer',id);marks.push(m);ng.appendChild(m)});
  var t=sv('text',{x:n.x,y:n.y+28});t.textContent=n.label;ng.appendChild(t);
  function pick(){if(sel)sel.setAttribute('class','node');sel=ng;ng.setAttribute('class','node sel');show(n)}
  ng.addEventListener('click',pick);ng.addEventListener('keydown',function(e){if(e.key==='Enter'||e.key===' '){e.preventDefault();pick()}});
  g.appendChild(ng)});
 function paint(){marks.forEach(function(m){m.style.display=on[m.getAttribute('data-layer')]?'':'none'})}
 function show(n){det.textContent='';det.hidden=false;var c=btn(T.close,function(){det.hidden=true;if(sel){sel.setAttribute('class','node');sel=null}});c.style.float='right';det.appendChild(c);det.appendChild(el('h2',null,n.label));var dl=el('dl');Object.keys(n.detail||{}).forEach(function(key){dl.appendChild(el('dt',null,key));dl.appendChild(el('dd',null,n.detail[key]==null?'':n.detail[key]))});det.appendChild(dl)}
 function apply(){g.setAttribute('transform','translate('+tx+' '+ty+') scale('+k+')')}
 function zoom(f){k=Math.max(0.2,Math.min(8,k*f));apply()}
 var drag=null;
 box.addEventListener('pointerdown',function(e){if(e.target.closest&&e.target.closest('.node'))return;drag={x:e.clientX,y:e.clientY,tx:tx,ty:ty};if(box.setPointerCapture)box.setPointerCapture(e.pointerId)});
 box.addEventListener('pointermove',function(e){if(!drag)return;var f=w/(box.clientWidth||w);tx=drag.tx+(e.clientX-drag.x)*f;ty=drag.ty+(e.clientY-drag.y)*f;apply()});
 box.addEventListener('pointerup',function(){drag=null});
 box.addEventListener('wheel',function(e){e.preventDefault();zoom(e.deltaY<0?1.1:0.9)},{passive:false});
 box.appendChild(s);wrap.appendChild(box);wrap.appendChild(det);main.appendChild(wrap);paint()}
function dash(d){
 var cs=el('div','cards');(d.cards||[]).forEach(function(c,i){var a=el('div','card'+(reduce?'':' anim'));if(!reduce)a.style.animationDelay=(i*60)+'ms';a.appendChild(el('span','l',c.label));var v=el('span','v'+(c.tone?' tone-'+c.tone:''),c.value);a.appendChild(v);if(c.foot)a.appendChild(el('span','f',c.foot));cs.appendChild(a);
  if(!reduce&&typeof c.value==='number'&&c.value>0&&window.requestAnimationFrame){var t0=0,end=c.value,fmt=new Intl.NumberFormat(P.lang);var step=function(ts){if(!t0)t0=ts;var p=Math.min(1,(ts-t0)/700);v.textContent=fmt.format(Math.round(end*p));if(p<1)requestAnimationFrame(step)};requestAnimationFrame(step)}else if(typeof c.value==='number'){v.textContent=new Intl.NumberFormat(P.lang).format(c.value)}});
 main.appendChild(cs);
 var ps=el('div','panels');
 (d.charts||[]).forEach(function(ch){var p=el('section','panel'+(reduce?'':' anim'));p.appendChild(el('h2',null,ch.title));var max=Math.max.apply(null,ch.items.map(function(x){return x.value}).concat([1]));var fills=[];
  ch.items.forEach(function(it){var r=el('div','hb'),tr=el('span','t'),f=el('span','f');r.appendChild(el('span',null,it.label));tr.appendChild(f);r.appendChild(tr);r.appendChild(el('span','num',new Intl.NumberFormat(P.lang).format(it.value)));p.appendChild(r);fills.push([f,(100*it.value/max).toFixed(1)+'%'])});
  ps.appendChild(p);var go=function(){fills.forEach(function(x){x[0].style.width=x[1]})};if(reduce||!window.requestAnimationFrame)go();else requestAnimationFrame(function(){requestAnimationFrame(go)})});
 (d.lists||[]).forEach(function(ls){var p=el('section','panel'+(reduce?'':' anim'));p.appendChild(el('h2',null,ls.title));ls.items.forEach(function(it){var r=el('div','li');if(it.tag)r.appendChild(el('span','tag'+(it.tone?' tone-'+it.tone:''),it.tag));var b=el('b',null,it.title);r.appendChild(b);if(it.where)r.appendChild(el('span','muted',it.where));p.appendChild(r)});ps.appendChild(p)});
 if(ps.childNodes.length)main.appendChild(ps)}
if(S.kind==='table')table(S.data);else if(S.kind==='map')map(S.data);else dash(S.data);
root.appendChild(el('footer','ft',T.footer));
})();`;

export function buildExportHtml(snapshot: ExportSnapshot, meta: ExportMeta): string {
  const accent = isHexColor(meta.brandColor) ? meta.brandColor : '#17614F';
  let dark = mix(accent, '#FFFFFF', 0.45);
  for (let i = 0; i < 10 && luminanceRough(dark) < 0.35; i++) dark = mix(dark, '#FFFFFF', 0.1);
  const css = CSS.replace(/ACCENT/g, accent).replace(/ACCDARK/g, dark);
  const brand = { logo: safeLogoDataUri(meta.logo), name: (meta.brandName ?? '').trim() || 'Hive' };
  const payload = safeJson({ snapshot, texts: meta.texts, lang: meta.lang, brand });
  // política do próprio arquivo: nada de fora (sem rede); estilo e script só os daqui
  const csp = "default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'";
  return [
    '<!doctype html>',
    `<html lang="${escapeHtml(meta.lang)}">`,
    '<head>',
    '<meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${csp}">`,
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(snapshot.title)}</title>`,
    `<style>${css}</style>`,
    '</head>',
    '<body>',
    '<div id="app"></div>',
    `<script type="application/json" id="hive-data">${payload}</script>`,
    `<script>${RUNTIME}</script>`,
    '</body>',
    '</html>',
  ].join('\n');
}

/** Luminância aproximada (0–1) para escolher o tom do acento no escuro. */
function luminanceRough(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
}

/** Baixa um texto como arquivo (Blob + link temporário). */
export function downloadText(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
